import React, { useState, useEffect, useRef } from "react";
import { FaTimes, FaRobot, FaPaperPlane, FaUserCircle, FaSpinner } from "react-icons/fa";
import { resume, name } from "../config/config";
import Gemini from "./Gemini";

const aiBaseURL = process.env.REACT_APP_AI_BASE_URL;

const InteractiveResume = ({ onClose }) => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `Hi! I'm ${name}'s AI Assistant. I can summarize this resume, extract key skills, or answer any specific questions about his experience. How can I help you evaluate his profile?`,
    },
  ]);
  const [input, setInput] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [sessionId, setSessionId] = useState("");
  
  // States for Resume Summary
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [resumeSummary, setResumeSummary] = useState("");
  const [isSummarizing, setIsSummarizing] = useState(false);
  
  const chatEndRef = useRef(null);

  const actionChips = [
    "Summarize backend experience",
    "List core tech stack",
    `Does ${name.split(" ")[0]} know Kafka?`,
    "Highlight top projects",
  ];

  useEffect(() => {
    // Generate a random session ID on mount
    setSessionId(Math.random().toString(36).substring(2, 15));
  }, []);

  useEffect(() => {
    // Scroll to bottom whenever messages change
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMessage = messageText;
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setInput("");
    setIsGenerating(true);

    let historyContext = "";
    if (messages.length > 0) {
      const recentMessages = messages.slice(-4);
      historyContext = recentMessages
        .map((m) => `${m.role === "user" ? "User" : "Assistant"}: ${m.content}`)
        .join("\n");
    }

    const finalPrompt = historyContext
      ? `Previous Conversation:\n${historyContext}\n\nCurrent Question: ${userMessage}`
      : userMessage;

    const authToken = localStorage.getItem("token") || process.env.REACT_APP_AUTH_TOKEN_KEY || "guddu1cse";
    const url = `${aiBaseURL}/api/chat/stream?prompt=${encodeURIComponent(finalPrompt)}&authToken=${authToken}&sessionId=${sessionId}`;

    try {
      // Add a placeholder message for the assistant
      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch AI stream");
      }

      setIsGenerating(false);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      let done = false;
      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          
          // Parse Server-Sent Events (SSE) lines
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data:')) {
              const text = line.substring(5).trim();
              if (text && !text.includes('[ERROR]')) {
                  // Append to the last message (which is the assistant's placeholder)
                  setMessages((prev) => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    lastMessage.content += (lastMessage.content ? " " : "") + text;
                    return newMessages;
                  });
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("AI Chat Error:", error);
      setIsGenerating(false);
      setMessages((prev) => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        lastMessage.content = "Sorry, I am having trouble connecting right now. Please try again later.";
        return newMessages;
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSend();
    }
  };

  const handleSummarizeResume = async () => {
    setIsSummaryModalOpen(true);
    setResumeSummary("");
    setIsSummarizing(true);
    const authToken = localStorage.getItem("token") || process.env.REACT_APP_AUTH_TOKEN_KEY || "guddu1cse";
    const finalPrompt = "summarize-resume-intent";
    const url = `${aiBaseURL}/api/chat/stream?prompt=${encodeURIComponent(finalPrompt)}&resumeUrl=${encodeURIComponent(resume)}&authToken=${authToken}&sessionId=${sessionId}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch AI stream");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      let done = false;
      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data:')) {
              const text = line.substring(5).trim();
              if (text && !text.includes('[ERROR]')) {
                  setResumeSummary(prev => prev + (prev ? " " : "") + text);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("AI Summarize Error:", error);
      setResumeSummary("Sorry, failed to summarize the resume. Please try again later.");
    } finally {
      setIsSummarizing(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      {/* Modal Container */}
      <div className="ios-glass w-full max-w-7xl h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row border border-white/10">
        
        {/* Header (Mobile Only for Close button, otherwise absolute positioned) */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-white hover:text-red-500 hover:scale-110 transition-transform ios-glass p-2 rounded-full z-50 shadow-lg"
          aria-label="Close Resume Viewer"
        >
          <FaTimes size={24} />
        </button>

        {/* Left Pane: PDF Viewer */}
        <div className="w-full md:w-3/5 h-1/2 md:h-full bg-black/40 relative border-b md:border-b-0 md:border-r border-white/10 flex flex-col">
          <div className="p-4 bg-black/40 backdrop-blur-xl border-b border-white/10 flex justify-between items-center z-10 shadow-xl">
             <h2 className="text-white font-bold text-lg">{name} Resume</h2>
             <button
               onClick={handleSummarizeResume}
               disabled={isSummarizing && isSummaryModalOpen}
               className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg shadow-lg transition-transform hover:scale-105 disabled:opacity-50 text-sm font-semibold"
             >
               <FaRobot />
               Summarize Resume
             </button>
          </div>
          <div className="flex-1 relative">
            <iframe
            src={`${resume}#navpanes=0&pagemode=none`}
            title={`${name} Resume`}
            className="w-full h-full border-none absolute inset-0"
          />
          </div>
        </div>

        {/* Right Pane: AI Chat */}
        <div className="w-full md:w-2/5 h-1/2 md:h-full flex flex-col bg-black/20 relative">
          {/* Header */}
          <div className="p-4 border-b border-white/10 bg-black/40 backdrop-blur-xl flex items-center gap-3 shadow-md z-10">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-[0_0_15px_rgba(99,102,241,0.5)] border border-white/20">
              <FaRobot size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 flex items-center gap-2">
                <Gemini message="Resume Assistant" fontSize={18} />
              </h3>
              <p className="text-xs text-indigo-300">Ask anything about the resume</p>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-5 space-y-5 scrollbar-thin scrollbar-thumb-indigo-500/50 scrollbar-track-transparent">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 ${
                  msg.role === "user" ? "flex-row-reverse" : "flex-row"
                }`}
              >
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-lg border ${
                    msg.role === "user"
                      ? "bg-purple-600 border-purple-400/50"
                      : "bg-indigo-600 border-indigo-400/50"
                  }`}
                >
                  {msg.role === "user" ? (
                    <FaUserCircle size={18} className="text-white" />
                  ) : (
                    <FaRobot size={16} className="text-white" />
                  )}
                </div>
                <div
                  className={`p-3.5 max-w-[85%] rounded-2xl text-[14px] leading-relaxed backdrop-blur-md shadow-lg ${
                    msg.role === "user"
                      ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-tr-sm border border-white/10"
                      : "bg-white/10 text-gray-100 rounded-tl-sm border border-white/10"
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            
            {/* Thinking Indicator */}
            {isGenerating && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 border border-indigo-400 flex items-center justify-center">
                  <FaRobot size={16} className="text-white" />
                </div>
                <div className="p-3.5 bg-white/5 backdrop-blur-md text-indigo-200 rounded-2xl rounded-tl-sm border border-white/10 flex items-center gap-3 text-sm shadow-lg">
                  <FaSpinner className="animate-spin text-indigo-400" />
                  Analyzing resume...
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Action Chips */}
          <div className="p-3 border-t border-white/10 bg-black/30 backdrop-blur-md flex overflow-x-auto gap-2 scrollbar-none z-10">
            {actionChips.map((chip, index) => (
              <button
                key={index}
                onClick={() => handleSend(chip)}
                disabled={isGenerating}
                className="whitespace-nowrap px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-[13px] font-medium text-indigo-100 transition-all disabled:opacity-50 shadow-sm"
              >
                {chip}
              </button>
            ))}
          </div>

          {/* Input Area */}
          <div className="p-4 bg-black/40 backdrop-blur-xl border-t border-white/10">
            <div className="flex items-center gap-2 bg-black/40 p-1.5 rounded-full border border-white/10 focus-within:border-indigo-400 focus-within:shadow-[0_0_20px_rgba(99,102,241,0.2)] transition-all">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about my experience..."
                className="flex-1 bg-transparent border-none text-white px-4 placeholder-gray-400 focus:outline-none text-[14px]"
                disabled={isGenerating}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || isGenerating}
                className="p-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(99,102,241,0.4)]"
              >
                <FaPaperPlane size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    {/* Summary Modal */}
    {isSummaryModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-gray-900 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden border border-indigo-500/50 flex flex-col max-h-[80vh]">
                <div className="p-4 bg-gray-800 border-b border-indigo-500/30 flex justify-between items-center">
                    <h3 className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 flex items-center gap-2">
                        <FaRobot size={20} className="text-indigo-400" /> AI Resume Summary
                    </h3>
                    <button
                        onClick={() => setIsSummaryModalOpen(false)}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        <FaTimes size={20} />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto text-gray-200 leading-relaxed space-y-4">
                    {resumeSummary ? (
                        resumeSummary.split('\n').map((paragraph, idx) => (
                            <p key={idx}>{paragraph}</p>
                        ))
                    ) : (
                        <div className="flex items-center gap-3 text-indigo-300">
                            <FaSpinner className="animate-spin" /> Generating summary...
                        </div>
                    )}
                </div>
            </div>
        </div>
    )}
    </>
  );
};

export default InteractiveResume;
